import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';

import TextInput from '../../../TextInput/TextInput';
import TextArea from '../../../TextArea/TextArea';
import * as Styled from './GiftCardForm.styled';

const requiredMsg = 'Required';
const invalidDateMsg = 'Please enter a valid date.';
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseISODateLocal = (v) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return null;
  const [y, m, d] = v.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const isParsableDate = (v) => {
  const d = parseISODateLocal(v);
  return d instanceof Date && !Number.isNaN(d.getTime());
};

const isTodayOrFuture = (v) => {
  const d = parseISODateLocal(v);
  if (!d) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d >= today;
};

const GiftCardSchema = Yup.object({
  recipientName: Yup.string().trim().required(requiredMsg),
  recipientEmail: Yup.string()
    .trim()
    .required(requiredMsg)
    .matches(emailRegex, 'Please enter a valid email address.'),
  sendDate: Yup.string()
    .required(requiredMsg)
    .test('is-date', invalidDateMsg, isParsableDate)
    .test('not-in-past', invalidDateMsg, isTodayOrFuture),
  giftMessage: Yup.string().max(500, 'Max 500 characters').nullable()
});

const defaultFormValues = {
  recipientName: '',
  recipientEmail: '',
  sendDate: format(new Date(), 'yyyy-MM-dd'),
  giftMessage: ''
};

const normalizeSendDate = (v) => {
  if (!v) return undefined;
  if (v instanceof Date && !Number.isNaN(v)) {
    return v.toISOString().slice(0, 10);
  }
  if (typeof v === 'string') {
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  return undefined;
};

const GiftCardForm = forwardRef(({ onSubmit, formValues }, ref) => {
  const formikPropsRef = useRef(null);

  const merged = { ...defaultFormValues, ...(formValues || {}) };
  const normalizedInitialValues = {
    ...merged,
    sendDate: normalizeSendDate(merged.sendDate) ?? defaultFormValues.sendDate
  };

  const handleSubmit = async (values, actions) => {
    try {
      if (typeof onSubmit === 'function') {
        await onSubmit(values, actions);
      }
    } finally {
      actions.setSubmitting(false);
    }
  };
  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (formikPropsRef.current) {
        formikPropsRef.current.submitForm();
      }
    },
    getValues: () => {
      if (formikPropsRef.current) {
        return formikPropsRef.current.values;
      }
      return null;
    },
    isValid: () => {
      if (formikPropsRef.current) {
        return formikPropsRef.current.isValid;
      }
      return false;
    },
    isDirty: () => {
      if (formikPropsRef.current) {
        return formikPropsRef.current.dirty;
      }
      return false;
    }
  }));

  const onSendDateBlur = (e, setFieldValue) => {
    if (!e?.target?.value) {
      const todayISO = format(new Date(), 'yyyy-MM-dd');
      setFieldValue('sendDate', todayISO);
      if (e?.target) e.target.value = todayISO;
    }
  };

  return (
    <Formik
      initialValues={normalizedInitialValues}
      validationSchema={GiftCardSchema}
      onSubmit={handleSubmit}
    >
      {(formikProps) => {
        formikPropsRef.current = formikProps;

        const { values, setFieldValue } = formikProps;

        return (
          <Form noValidate>
            <Styled.FormWrap>
              <TextInput
                id="recipient_name"
                label="Recipient Name"
                name="recipientName"
                required
              />
              <TextInput
                id="recipient_email"
                label="Recipient Email"
                name="recipientEmail"
                type="email"
                required
              />
              <TextInput
                id="send_date"
                label="Send Date"
                name="sendDate"
                type="date"
                required
                value={values.sendDate}
                onBlur={(e) => onSendDateBlur(e, setFieldValue)}
              />
              <TextArea
                id="gift_message"
                name="giftMessage"
                label="Gift Message"
              />
            </Styled.FormWrap>
          </Form>
        );
      }}
    </Formik>
  );
});

GiftCardForm.propTypes = {
  onSubmit: PropTypes.func,
  formValues: PropTypes.shape({
    recipientName: PropTypes.string,
    recipientEmail: PropTypes.string,
    sendDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ]),
    giftMessage: PropTypes.string
  })
};

GiftCardForm.defaultProps = {
  onSubmit: undefined,
  formValues: undefined
};

export default GiftCardForm;
